import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from 'src/state/auth/auth';
import { get, every } from 'lodash';
import type { OrgUnitActions, Permissions } from 'src/graphql/_generated';
import { useOrganisationAccessCheckQuery, useWorkspaceAccessCheckQuery } from 'src/graphql/_generated';
import Loader from 'src/components/Loader/Loader';

type OrgUnitPermissionKey = keyof Omit<OrgUnitActions, '__typename'>;
type ProfilePermissionKey = keyof Omit<Permissions, '__typename'>;

interface AccessGuardProps {
  children: React.ReactNode;
  activeOrgUnitRecordType?: 'O' | 'W';
  requiredPermissions: {
    orgUnit?: OrgUnitPermissionKey[];
    profile?: ProfilePermissionKey[];
  };
}

export const AccessGuard: React.FC<AccessGuardProps> = ({
  children,
  requiredPermissions = {},
  activeOrgUnitRecordType = 'W',
}) => {
  const { id } = useParams<{ id?: string }>();
  const { profile } = useAuth();

  const isOrgRoute = activeOrgUnitRecordType === 'O';

  const { data: orgAccessData, loading: orgLoading } = useOrganisationAccessCheckQuery({
    variables: { id },
    skip: !id || !isOrgRoute,
  });

  const { data: wsAccessData, loading: wsLoading } = useWorkspaceAccessCheckQuery({
    variables: { id },
    skip: !id || isOrgRoute,
  });

  if (id && ((orgLoading && !orgAccessData) || (wsLoading && !wsAccessData))) {
    return <Loader />;
  }

  const getOrgUnitPermission = (permission: OrgUnitPermissionKey): boolean => {
    if (id) {
      if (isOrgRoute) {
        if (!orgAccessData?.organisation) return false;
        return get(orgAccessData, `organisation.actionsOnThisOrganisation.${permission}`, false);
      } else {
        if (!wsAccessData?.workspace) return false;
        return get(wsAccessData, `workspace.actionsOnThisWorkspace.${permission}`, false);
      }
    }

    if (activeOrgUnitRecordType === 'W') {
      return get(profile, `activeWorkspace.actionsOnThisWorkspace.${permission}`, false);
    }

    return get(profile, `activeOrganisation.actionsOnThisOrganisation.${permission}`, false);
  };

  const orgUnitPermissionsPassed = requiredPermissions.orgUnit
    ? every(requiredPermissions.orgUnit, (el) => getOrgUnitPermission(el))
    : true;

  const profilePermissionsPassed = requiredPermissions.profile
    ? every(requiredPermissions.profile, (el) => {
        const result = get(profile, `permissions.${el}`);

        return result;
      })
    : true;

  const hasAccess = orgUnitPermissionsPassed && profilePermissionsPassed;

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
